import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ProductWithCalculations } from '../shared/types/inventory';
import { useSettings } from '../shared/hooks/useSettings';
import { ProductHeader } from './ProductHeader';
import { AquariumTank } from './AquariumTank';
import { StockMarkers } from './StockMarkers';
import { MetricsPanel } from './MetricsPanel';
import { LegendPanel } from './LegendPanel';
import { InfoCallout } from './InfoCallout';
import {CardHelp} from '../shared/components/CardHelp';

interface ProductDetailModalProps {
  product: ProductWithCalculations;
  onClose: () => void;
}

export const ProductDetailModal = ({ product, onClose }: ProductDetailModalProps) => {
  const { settings } = useSettings();

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const percentageOfTarget = (value: number) => {
    if (!Number.isFinite(product.estoqueAlvo) || product.estoqueAlvo <= 0) {
      return value > 0 ? 100 : 0;
    }
    return Math.max(0, Math.min(100, (value / product.estoqueAlvo) * 100));
  };
  const currentStockPercentage = percentageOfTarget(product.estoqueAtual);
  const ropPercentage = percentageOfTarget(product.rop);
  const currentStockY = 80 + (340 * (100 - currentStockPercentage) / 100);
  const ropY = 80 + (340 * (100 - ropPercentage) / 100);

  const getWaterColor = () => {
    switch (product.status) {
      case 'ruptura':
      case 'iminente':
        return '#dc2626';
      case 'critica':
        return '#f59e0b';
      case 'minimo':
        return '#eab308';
      case 'excesso':
        return '#06b6d4';
      default:
        return '#3b82f6';
    }
  };

  const visualMetrics = [
    {label: 'Estoque alvo', value: product.estoqueAlvo.toFixed(0), unit: 'un', color: 'text-emerald-700', help: 'Quantidade planejada para atender a cobertura alvo.'},
    {label: 'Estoque atual', value: product.estoqueAtual.toFixed(0), unit: 'un', color: 'text-blue-700', help: 'Quantidade disponível no estoque neste momento.'},
    {label: 'ROP', value: product.rop.toFixed(0), unit: 'un', color: 'text-amber-700', help: 'Ponto de pedido = demanda no lead time + estoque de segurança.'},
    {label: 'Seguran\u00e7a', value: product.ss.toFixed(0), unit: 'un', color: 'text-slate-700', help: 'Reserva calculada para variações e imprevistos.'},
    {label: 'CMD', value: product.cmd.toFixed(2), unit: 'un/dia', color: 'text-violet-700', help: 'Consumo médio diário = vendas do período ÷ dias do período.'},
  ];

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[20000] flex h-[100dvh] items-center justify-center overflow-hidden bg-black/50 p-2 backdrop-blur-sm sm:p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-full min-h-0 w-full max-w-7xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <ProductHeader
          name={product.nome}
          ean={product.ean}
          status={product.status}
          statusLabel={product.statusLabel}
          onClose={onClose}
        />

        <div className="min-h-0 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3 xl:gap-8">
            <div className="space-y-6 xl:col-span-2">
              <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 p-4 sm:p-6 lg:p-8">
                <h3 className="mb-4 text-center text-base font-semibold text-slate-900 sm:mb-6 sm:text-lg">
                  Visualização de Estoque
                </h3>

                <AquariumTank
                  waterHeight={currentStockPercentage}
                  waterColor={getWaterColor()}
                >
                  <StockMarkers
                    ropPercentage={ropPercentage}
                    targetStockValue={product.estoqueAlvo}
                    ropValue={product.rop}
                  />
                  <InfoCallout
                    position="left"
                    x={100}
                    y={80}
                    labelY={105}
                    title="Estoque Alvo"
                    value={`${product.estoqueAlvo.toFixed(0)} un`}
                    description={`${settings.diasCoberturaAlvo} dias`}
                    color="#10b981"
                  />
                  <InfoCallout
                    position="left"
                    x={100}
                    y={ropY}
                    labelY={240}
                    title="ROP"
                    value={`${product.rop.toFixed(0)} un`}
                    description="Ponto de pedido"
                    color="#f59e0b"
                  />
                  <InfoCallout
                    position="right"
                    x={500}
                    y={currentStockY}
                    labelY={175}
                    title="Estoque Atual"
                    value={`${product.estoqueAtual.toFixed(0)} un`}
                    description={'Dispon\u00edvel agora'}
                    color="#2563eb"
                  />
                  <InfoCallout
                    position="left"
                    x={100}
                    y={335}
                    labelY={380}
                    title={'Seguran\u00e7a'}
                    value={`${product.ss.toFixed(0)} un`}
                    description="Reserva"
                    color="#64748b"
                  />
                  <InfoCallout
                    position="right"
                    x={500}
                    y={250}
                    labelY={325}
                    title="CMD"
                    value={`${product.cmd.toFixed(2)} un/dia`}
                    description={'M\u00e9dia di\u00e1ria'}
                    color="#7c3aed"
                  />
                </AquariumTank>
                <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                  {visualMetrics.map((metric) => (
                    <div key={metric.label} className="relative rounded-xl border border-slate-200 bg-white/90 p-3 text-center shadow-sm">
                      <CardHelp content={metric.help} className="absolute right-1.5 top-1.5" />
                      <p className="pr-6 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{metric.label}</p>
                      <p className={`mt-1 text-lg font-bold tabular-nums ${metric.color}`}>
                        {metric.value} <span className="text-xs font-medium text-slate-400">{metric.unit}</span>
                      </p>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-center text-xs text-slate-500">
                  Estoque atual: {currentStockPercentage.toFixed(1)}% do alvo · Cobertura alvo: {settings.diasCoberturaAlvo} dias
                </p>
              </div>

              <LegendPanel />
            </div>

            <div className="xl:col-span-1">
              <MetricsPanel
                product={product}
                leadTime={product.leadTime ?? settings.leadTime}
                targetCoverage={settings.diasCoberturaAlvo}
              />
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
